// src/pages/CompilationsPage.js — Real API connected
import React, { useState, useEffect } from 'react';
import { compilationsAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { decrypt, decryptData } from '../utils/security';

const GRADIENTS = [
  'linear-gradient(90deg,#A3B4CC,#8898B8)',
  'linear-gradient(90deg,#D4A5A3,#C08A88)',
  'linear-gradient(90deg,#A3C4B4,#88AE9E)',
  'linear-gradient(90deg,#C4B488,#AE9E74)',
  'linear-gradient(90deg,#B4A3C8,#9E88B4)',
  'linear-gradient(90deg,#C4A3B4,#AE8E9E)',
  'linear-gradient(90deg,#C4B4A3,#AE9E8E)',
  'linear-gradient(90deg,#A8B8C8,#9098B0)',
];

const CompilationsPage = ({ onOpenBook }) => {
  const [books, setBooks]       = useState([]);
  const [topics, setTopics]     = useState([]);
  const [subject, setSubject]   = useState('All Subjects');
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [pagination, setPag]    = useState(null);
  const { getToken } = useApp();

  // Load subjects/topics for filter
  useEffect(() => {
    (async () => {
      try {
        const res = await compilationsAPI.getSubjects();
        setTopics(['All Subjects', ...(res.data?.topics || [])]);
      } catch(e) { console.error(e); }
    })();
  }, []);

  // Load books
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { page, limit: 15 };
        if (subject !== 'All Subjects') params.subject = subject;
        const token = await getToken();
        const res = await compilationsAPI.getAll(token, params);
        const decrypted = decryptData(res.data || []);
        setBooks(decrypted);
        setPag(res.pagination);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [getToken, subject, page]);

  const handleSubject = (s) => { setSubject(s); setPage(1); };

  return (
    <div style={{ background:'var(--off-white)', minHeight:'100vh', fontFamily:'Nunito,system-ui,sans-serif' }}>

      {/* Header */}
      <div className="px-4 py-3 border-b" style={{ background:'var(--slate-pale)', borderColor:'var(--slate-lt)' }}>
        <div className="font-display font-bold" style={{ fontSize:16, color:'var(--slate)', fontFamily:'Playfair Display,serif' }}>
          Article Compilations
        </div>
        <div style={{ fontSize:9, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', marginTop:2 }}>
          Curated by AIR Editorial Board · AIR-exclusive · {pagination?.total || 0} titles
        </div>
      </div>

      {/* Subject filter chips */}
      <div className="flex gap-2 px-4 py-2 bg-white border-b overflow-x-auto no-scrollbar"
        style={{ borderColor:'var(--border)' }}>
        {topics.map(s => (
          <button key={s} onClick={() => handleSubject(s)}
            className="flex-shrink-0 px-3 py-1 rounded-full font-bold cursor-pointer"
            style={{
              fontSize:10, fontFamily:'Nunito,sans-serif',
              background: subject === s ? 'var(--slate)' : 'white',
              color:      subject === s ? 'white'        : 'var(--ink-mid)',
              border:     subject === s ? 'none'         : '1px solid var(--border)',
            }}>
            {s}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 gap-3">
          <div className="rounded-full border-t-2 animate-spin"
            style={{ width:24, height:24, border:'2px solid var(--border)', borderTopColor:'var(--slate)' }}/>
          <span style={{ fontSize:12, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>Loading…</span>
        </div>
      )}

      {/* Cards — exact .ccard design */}
      {!loading && (
        <div style={{ padding:'4px 0' }}>
          {books.length === 0 ? (
            <div className="text-center py-16">
              <div style={{ fontSize:40, opacity:.3, marginBottom:10 }}>📋</div>
              <div style={{ fontSize:14, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>No compilations found</div>
            </div>
          ) : books.map((book, i) => {
            const title   = decrypt(book.book_name || book.book_title || '—');
            const band    = GRADIENTS[book._id % GRADIENTS.length];
            const authors = (book.authors || []).map(a => a.name).join(', ') || 'AIR Editorial Board';
            const topics  = (book.topics  || []).map(t => t.name).join(', ');
            const desc    = book.book_short_description
              ? decrypt(book.book_short_description).replace(/<[^>]*>/g, '').substring(0, 150)
              : topics || 'AIR Legal Compilation';

            return (
              <div key={book._id} className="mx-4 my-3 bg-white rounded-xl overflow-hidden border cursor-pointer animate-slide-up"
                style={{ boxShadow:'var(--shadow-xs)', borderColor:'var(--border)', animationDelay:`${i*0.05}s` }}
                onMouseEnter={e => e.currentTarget.style.boxShadow='var(--shadow-sm)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow='var(--shadow-xs)'}
                onClick={() => onOpenBook && onOpenBook(book)}>

                {/* Color band */}
                <div style={{ height:5, background:band }}/>

                {/* Body */}
                <div style={{ padding:'11px 13px' }}>
                  {/* Title */}
                  <div className="font-display font-bold mb-1"
                    style={{ fontSize:13, color:'var(--ink)', fontFamily:'Playfair Display,serif', lineHeight:1.35 }}>
                    {title}
                  </div>
                  {/* Meta */}
                  <div className="flex gap-3 mb-2" style={{ fontSize:9, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>
                    {book.book_publication_date && <span>📅 {new Date(book.book_publication_date).getFullYear()}</span>}
                    {book.category?.name && <span>📂 {book.category.name}</span>}
                    {book.state && <span style={{ color: book.state==='accept' ? 'var(--sage)' : 'var(--ink-faint)' }}>● {book.state}</span>}
                  </div>
                  {/* Description */}
                  <div className="mb-2" style={{ fontSize:11, color:'var(--ink-mid)', lineHeight:1.5, fontFamily:'Nunito,sans-serif' }}>
                    {desc}…
                  </div>
                  {/* Topics chips */}
                  {book.topics?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {book.topics.slice(0,3).map(t => (
                        <span key={t.name} style={{ fontSize:9, color:'var(--slate)', background:'var(--slate-pale)', padding:'1px 6px', borderRadius:8, fontFamily:'Nunito,sans-serif' }}>
                          {t.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div style={{ fontSize:9, color:'var(--ink-faint)', fontFamily:'Nunito,sans-serif' }}>
                      {authors.substring(0, 40)}
                    </div>
                    <button className="text-white rounded font-bold border-0 cursor-pointer"
                      style={{ background:'var(--slate)', fontSize:9, padding:'4px 9px', fontFamily:'Nunito,sans-serif' }}
                      onClick={e => { e.stopPropagation(); onOpenBook && onOpenBook(book); }}>
                      Open →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2 px-4 py-4">
              <button disabled={!pagination.has_prev} onClick={() => setPage(p => p-1)}
                className="px-4 py-2 bg-white border rounded-lg text-xs font-bold disabled:opacity-40 cursor-pointer"
                style={{ borderColor:'var(--border)', color:'var(--ink-mid)', fontFamily:'Nunito,sans-serif' }}>‹ Prev</button>
              <span className="px-4 py-2 text-xs" style={{ color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>
                {pagination.page} / {pagination.total_pages}
              </span>
              <button disabled={!pagination.has_next} onClick={() => setPage(p => p+1)}
                className="px-4 py-2 bg-white border rounded-lg text-xs font-bold disabled:opacity-40 cursor-pointer"
                style={{ borderColor:'var(--border)', color:'var(--ink-mid)', fontFamily:'Nunito,sans-serif' }}>Next ›</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompilationsPage;